// Agents page activity data layer (#5206, epic #5200). Reads the one aggregate
// `GET /v1/agents/activity` (`read:tasks`): per roster agent, tasks running now,
// when it last ran, the last 7 days' worked/failed counts, and — on the agents a
// mode governs — whether that mode is on. The page that renders it is #5209.
//
// POLLED, not streamed: "last run" and the 7-day window are rolling aggregates that
// move with no single event, so a 30 s poll (the overview strip's cadence) is enough
// and no SSE topic lands. The query's `signal` is handed to `fetch`, so a poll the
// page no longer wants (an org switch, an unmount) is aborted rather than left to
// land into a cache nobody reads. The body is PARSED through the domain's own wire
// schema, never cast — the server's type, not a mirror — and that schema is strict,
// so a mode's tool list arriving here would be a loud parse failure (D11).

import { type AgentActivityWire, AgentActivityWireSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** `GET /v1/agents/activity` — the server's own wire type. */
export type AgentActivity = AgentActivityWire;
/** One agent's row of {@link AgentActivity}. */
export type AgentActivityEntry = AgentActivityWire['agents'][number];

export const AGENT_ACTIVITY_QUERY_KEY = 'agent_activity';

/** How often the Agents page re-reads. Never faster: the read is an aggregate over every task. */
export const AGENT_ACTIVITY_POLL_MS = 30_000;

/** One read of the activity aggregate, abortable through `signal`. */
export function fetchAgentActivity(
  token: string | null,
  signal?: AbortSignal,
): Promise<AgentActivity> {
  return apiFetch('/v1/agents/activity', token, signal ? { signal } : undefined, {
    schema: AgentActivityWireSchema,
  });
}

/**
 * The account's agent activity. Idle with no session; polled every
 * {@link AGENT_ACTIVITY_POLL_MS}. Keyed by the active org so an org switch re-reads
 * under the new tenant instead of showing the previous one's numbers.
 */
export function useAgentActivity(token: string | null = SESSION_TOKEN) {
  return createQuery<AgentActivity>(() => ({
    queryKey: [AGENT_ACTIVITY_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    refetchInterval: AGENT_ACTIVITY_POLL_MS,
    queryFn: ({ signal }) => fetchAgentActivity(token, signal),
  }));
}
